import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { RecentlyViewedContext } from '@components/RecentlyViewed/RecentlyViewedProvider'

import Skeleton from '@mui/material/Skeleton'
import _ from 'lodash'
import SwiperClass, { SwiperOptions } from 'swiper'
import { Swiper, SwiperSlide } from 'swiper/react'
import { maybe } from '../../utils/misc'
import ReflectionCard from '@components/Product/ReflectionCard'
import useOpenCardPanel from '@components/Product/ProductCardPanel/useOpenCardPanel'
import {
  ProductReflectionsQuery,
  useProductReflectionsLazyQuery,
} from '@graphql/queries/__generated__/reflection.generated'
import { useTranslation } from 'react-i18next'
import { useIsVisible } from '../../hooks/useIsVisible'
import { Ordering } from '../../../generated/gqlTypes'

const MAX_SHOW_PRODUCTS = 18

// Same shape as Category's card skeleton (SpecificsList).
const RecentlyViewedSkeleton: React.FC<{ swiperOptions: SwiperOptions }> = ({ swiperOptions }) => {
  return (
    <Swiper {...swiperOptions} className='recently-viewed__swiper'>
      {_.range(6).map(idx => (
        <SwiperSlide key={idx}>
          <div className='product-card'>
            <div className='relative mb-10 rounded-lg overflow-hidden' style={{ paddingBottom: '138%' }}>
              <Skeleton className='absolute top-0 bottom-0 left-0 right-0' variant='rectangular' height='100%' />
            </div>
            <Skeleton variant='text' />
            <Skeleton variant='text' />
          </div>
        </SwiperSlide>
      ))}
    </Swiper>
  )
}

interface RecentlyViewedProps {
  slidesPerView?: number
  align?: 'center' | 'left' | 'right'
  // Wholesale mode (Category): wholesale products only, with wholesale prices and quick add to the wholesale cart.
  wholesale?: boolean
}

const RecentlyViewed: React.FC<RecentlyViewedProps> = ({ slidesPerView, align, wholesale = false }) => {
  const { t } = useTranslation()
  const ref = useRef()
  const isVisible = useIsVisible(ref, { threshold: 0.25 })
  const { value: storageIds } = useContext(RecentlyViewedContext)
  const [swiper, setSwiper] = useState<SwiperClass | null>(null)
  // Only one card keeps its quick add-to-cart panel open.
  const { openPanelSpecId, onCardPointerDown, onTogglePanel } = useOpenCardPanel()
  // The ids are frozen at the first load: opening a card's product dialog marks that product as viewed, which
  // changes `storageIds`; refetching with them would reshuffle (`sampleSize`) and remount the cards, closing
  // the dialog that lives inside the clicked card.
  const [queryIds, setQueryIds] = useState<number[] | null>(null)
  // The same query as Category's grid, so the cards get everything their quick add-to-cart needs.
  const [loadReflections, reflectionsQueryResult] = useProductReflectionsLazyQuery({
    variables: {
      first: 20,
      idIn: (queryIds ?? storageIds).map(id => `${id}`),
      orderBy: [{ id: Ordering.Desc }],
      isWholesale: wholesale || null,
      wholesale,
    },
    // Deliberately bypasses the cache: this query shares the `specifics` field policy with
    // Category's paginated query, and writing its result into the cache was invalidating
    // Category's cached pages, forcing them to refetch from page 1.
    fetchPolicy: 'no-cache',
  })

  useEffect(() => {
    if (isVisible && storageIds.length && !queryIds) {
      setQueryIds(storageIds)
      loadReflections()
    }
  }, [isVisible, queryIds, storageIds.length])

  // The carousel must not slide the card away from under an open quick add-to-cart form.
  useEffect(() => {
    if (openPanelSpecId) swiper?.autoplay?.stop()
  }, [openPanelSpecId, swiper])

  const reflections: ProductReflectionsQuery['specifics']['edges'][0]['node'][] = useMemo(
    () =>
      maybe(
        () =>
          _.sampleSize(
            reflectionsQueryResult.data.specifics.edges.map(edge => edge.node),
            MAX_SHOW_PRODUCTS
          ),
        []
      ),
    [reflectionsQueryResult.data]
  )

  const swiperOptions: SwiperOptions = {
    // Category's card grid gap (`.product-grid--cards`).
    spaceBetween: 16,
    slidesPerView: slidesPerView || 5,
    pagination: {
      clickable: true,
      el: `#swiper-pagination-recently-viewed`,
    },
    watchSlidesProgress: true,
    loop: false,
    allowSlideNext: reflections.length > 1,
    allowSlidePrev: reflections.length > 1,
    // Dragging inside the quick add-to-cart form (quantity, selects) must not move the carousel.
    noSwipingSelector: '.product-card-panel',
    autoplay: {
      disableOnInteraction: true,
      delay: 5000,
      waitForTransition: true,
    },

    // Matches Category's product-grid_5 (Image Size: Medium) column breakpoints.
    breakpoints: {
      0: {
        slidesPerView: 2,
        slidesPerGroup: 2,
        spaceBetween: 8,
        speed: 300,
      },
      600: {
        slidesPerView: 3,
        slidesPerGroup: 3,
        speed: 1000,
      },
      900: {
        slidesPerView: 4,
        slidesPerGroup: 4,
        speed: 1000,
      },
      1280: {
        slidesPerView: slidesPerView || 5,
        slidesPerGroup: slidesPerView || 5,
        speed: 1000,
      },
    },
  }
  return (
    <section className='recently-viewed my-20' ref={ref}>
      <div className='pt-20 px-12'>
        <h3
          className={`mb-40 font-medium text-4xl md:text-5xl uppercase leading-24 ${
            align ? `text-${align}` : 'text-left'
          }`}
        >
          {t('title.recentlyViewed', 'Recently Viewed')}
        </h3>
      </div>
      <div className='relative px-12 pb-20'>
        {!reflections.length && reflectionsQueryResult.loading && (
          <RecentlyViewedSkeleton swiperOptions={swiperOptions} />
        )}
        <Swiper {...swiperOptions} className='recently-viewed__swiper' onSwiper={setSwiper}>
          {reflections.map((spec, idx) => (
            <SwiperSlide key={spec.id}>
              <div
                className={`product-thumb text-center product-card ${
                  openPanelSpecId === spec.id ? 'product-card--active' : ''
                }`}
                onPointerDown={onCardPointerDown(spec.id)}
              >
                <ReflectionCard
                  spec={spec}
                  onGooglePushDataLayer={() => null}
                  showBigImg
                  idx={idx}
                  idPrefix='recently-viewed-'
                  showFavoritesButton
                  cardView
                  wholesale={wholesale}
                  isPanelOpen={openPanelSpecId === spec.id}
                  onTogglePanel={onTogglePanel(spec.id)}
                />
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
        <div
          id={`swiper-pagination-recently-viewed`}
          className={`swiper-pagination swiper-pagination-clickable swiper-pagination-bullets w-full ${
            reflections.length > 1 ? 'block' : 'hidden'
          } md:hidden`}
        />
      </div>
    </section>
  )
}

export default RecentlyViewed
